// AI Assistant Panel for GrapesJS
// Provides intelligent suggestions for components, layouts, and content

import type { GrapesJSEditor } from '../types/grapesjs';

interface AIAssistantOptions {
  panelId?: string;
  panelTitle?: string;
  panelIcon?: string;
  zIndex?: number;
  enableComponentSuggestions?: boolean;
  enableLayoutSuggestions?: boolean;
  enableContentSuggestions?: boolean;
  enableRealEstateSuggestions?: boolean;
}

export default function aiAssistantPanel(editor: GrapesJSEditor, opts: AIAssistantOptions = {}) {
  const options = {
    panelId: opts.panelId || 'ai-assistant-panel',
    panelTitle: opts.panelTitle || 'AI Assistant',
    panelIcon: opts.panelIcon || '🤖',
    zIndex: opts.zIndex || 1000,
    enableComponentSuggestions: opts.enableComponentSuggestions !== false,
    enableLayoutSuggestions: opts.enableLayoutSuggestions !== false,
    enableContentSuggestions: opts.enableContentSuggestions !== false,
    enableRealEstateSuggestions: opts.enableRealEstateSuggestions !== false
  };

  let panel: HTMLElement | null = null;
  let isOpen: boolean = false;

  // Create the AI assistant panel
  function createPanel(): HTMLElement {
    panel = document.createElement('div');
    panel.id = options.panelId;
    panel.className = 'ai-assistant-panel';
    panel.innerHTML = `
      <div class="ai-assistant-header">
        <div class="ai-assistant-title">
          <span class="ai-assistant-icon">${options.panelIcon}</span>
          <h3>${options.panelTitle}</h3>
        </div>
        <button class="ai-assistant-close">×</button>
      </div>

      <div class="ai-assistant-content">
        <div class="ai-assistant-section">
          <h4>What would you like to create?</h4>
          <div class="ai-assistant-actions">
            <button class="ai-action-btn" data-action="suggestComponents">
              <span class="ai-action-icon">🏗️</span>
              Suggest Components
            </button>
            <button class="ai-action-btn" data-action="suggestLayout">
              <span class="ai-action-icon">📐</span>
              Suggest Layout
            </button>
            <button class="ai-action-btn" data-action="generateContent">
              <span class="ai-action-icon">✍️</span>
              Generate Content
            </button>
            <button class="ai-action-btn" data-action="suggestRealEstate">
              <span class="ai-action-icon">🏠</span>
              Real Estate Help
            </button>
          </div>
        </div>

        <div class="ai-assistant-section">
          <h4>Quick Actions</h4>
          <div class="ai-quick-actions">
            <button class="ai-quick-btn" data-action="createHeroSection">
              Create Hero Section
            </button>
            <button class="ai-quick-btn" data-action="createPropertyGrid">
              Create Property Grid
            </button>
            <button class="ai-quick-btn" data-action="createContactForm">
              Create Contact Form
            </button>
            <button class="ai-quick-btn" data-action="createNavigation">
              Create Navigation
            </button>
          </div>
        </div>
        
        <div class="ai-assistant-section">
          <h4>Context Information</h4>
          <div class="ai-context-info">
            <div class="context-item">
              <strong>Current Device:</strong> <span id="current-device">Desktop</span>
            </div>
            <div class="context-item">
              <strong>Selected Component:</strong> <span id="selected-component">None</span>
            </div>
            <div class="context-item">
              <strong>Available Components:</strong> <span id="available-components">Loading...</span>
            </div>
          </div>
        </div>
        
        <div class="ai-assistant-section">
          <h4>Suggestions</h4>
          <div id="ai-suggestions" class="ai-suggestions">
            <p>Click an action above to get started...</p>
          </div>
        </div>
      </div>
    `;

    // Add styles
    const styles = document.createElement('style');
    styles.textContent = `
      .ai-assistant-panel {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 500px;
        max-height: 80vh;
        background: white;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        z-index: ${options.zIndex};
        display: none;
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      
      .ai-assistant-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
      }
      
      .ai-assistant-title {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      
      .ai-assistant-icon {
        font-size: 24px;
      }
      
      .ai-assistant-title h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
      }
      
      .ai-assistant-close {
        background: none;
        border: none;
        color: white;
        font-size: 24px;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background 0.2s;
      }
      
      .ai-assistant-close:hover {
        background: rgba(255,255,255,0.2);
      }
      
      .ai-assistant-content {
        padding: 20px;
        max-height: calc(80vh - 80px);
        overflow-y: auto;
      }
      
      .ai-assistant-section {
        margin-bottom: 24px;
      }
      
      .ai-assistant-section h4 {
        margin: 0 0 12px 0;
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }
      
      .ai-assistant-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      
      .ai-action-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 16px;
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        background: white;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 14px;
        font-weight: 500;
      }
      
      .ai-action-btn:hover {
        border-color: #667eea;
        background: #f8f9ff;
        transform: translateY(-2px);
      }
      
      .ai-action-icon {
        font-size: 24px;
      }
      
      .ai-quick-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
      }
      
      .ai-quick-btn {
        padding: 12px 16px;
        border: 1px solid #e1e5e9;
        border-radius: 6px;
        background: white;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 13px;
        font-weight: 500;
      }
      
      .ai-quick-btn:hover {
        border-color: #667eea;
        background: #f8f9ff;
      }
      
      .ai-context-info {
        background: #f8f9fa;
        padding: 16px;
        border-radius: 8px;
      }
      
      .context-item {
        margin-bottom: 8px;
        font-size: 14px;
      }
      
      .context-item:last-child {
        margin-bottom: 0;
      }
      
      .ai-suggestions {
        background: #f8f9fa;
        padding: 16px;
        border-radius: 8px;
        min-height: 100px;
      }
      
      .suggestion-item {
        background: white;
        padding: 12px;
        border-radius: 6px;
        margin-bottom: 8px;
        border-left: 4px solid #667eea;
      }
      
      .suggestion-item h5 {
        margin: 0 0 4px 0;
        font-size: 14px;
        font-weight: 600;
      }
      
      .suggestion-item p {
        margin: 0;
        font-size: 13px;
        color: #666;
      }
      
      .suggestion-actions {
        margin-top: 8px;
        display: flex;
        gap: 8px;
      }
      
      .suggestion-btn {
        padding: 4px 8px;
        border: 1px solid #667eea;
        border-radius: 4px;
        background: white;
        color: #667eea;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;
      }
      
      .suggestion-btn:hover {
        background: #667eea;
        color: white;
      }
    `;
    document.head.appendChild(styles);

    wireStaticButtons(panel);
    return panel;
  }

  const actionMap: Record<string, () => void> = {
    suggestComponents,
    suggestLayout,
    generateContent,
    suggestRealEstate,
    createHeroSection,
    createPropertyGrid,
    createContactForm,
    createNavigation,
  };

  function wireStaticButtons(p: HTMLElement) {
    p.querySelector('.ai-assistant-close')?.addEventListener('click', closePanel);

    p.querySelectorAll('[data-action]').forEach(btn => {
      const action = (btn as HTMLElement).dataset.action;
      if (action && actionMap[action]) {
        btn.addEventListener('click', actionMap[action]);
      }
    });

    // Event delegation for dynamically generated suggestion items
    p.querySelector('#ai-suggestions')?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-suggestion-action], [data-learn-more]') as HTMLElement | null;
      if (!btn) return;
      if (btn.dataset.suggestionAction) {
        const fn = actionMap[btn.dataset.suggestionAction];
        if (fn) fn();
        else console.log('Action not implemented:', btn.dataset.suggestionAction);
      } else if (btn.dataset.learnMore) {
        showNotification(`Learn more about: ${btn.dataset.learnMore}`, 'info');
      }
    });
  }

  // Show the panel
  function showPanel() {
    if (!panel) {
      panel = createPanel();
      document.body.appendChild(panel);
    }
    
    panel.style.display = 'block';
    isOpen = true;
    
    // Update context information
    updateContextInfo();
  }

  // Close the panel
  function closePanel() {
    if (panel) {
      panel.style.display = 'none';
      isOpen = false;
    }
  }

  // Update context information
  function updateContextInfo() {
    const deviceElement = document.getElementById('current-device');
    const selectedElement = document.getElementById('selected-component');
    const availableElement = document.getElementById('available-components');
    
    if (deviceElement) {
      deviceElement.textContent = editor.getDevice() || 'Desktop';
    }
    
    if (selectedElement) {
      const selected = editor.getSelected();
      selectedElement.textContent = selected ? 
        `${selected.get('tagName')} (${selected.get('className') || 'no class'})` : 
        'None';
    }
    
    if (availableElement && window.aiContext) {
      const componentCount = Object.values(window.aiContext.availableComponents)
        .reduce((total, category) => total + Object.keys(category).length, 0);
      availableElement.textContent = `${componentCount} components available`;
    }
  }

  // Suggest components based on context
  function suggestComponents() {
    const suggestions = [];
    const selected = editor.getSelected();
    const device = editor.getDevice();
    
    // Get available blocks from the block manager
    const blockManager = editor.BlockManager;
    const blocks = blockManager.getAll();
    
    // Basic suggestions based on common patterns
    if (!selected || selected.get('tagName') === 'body') {
      // Look for hero-related blocks
      blocks.forEach(block => {
        const content = block.get('content');
        if (content && typeof content === 'string' && content.includes('hero')) {
          suggestions.push({
            title: block.get('label') || 'Hero Section',
            description: 'Create a compelling hero section',
            action: 'createHeroSection',
            reason: 'Perfect for landing pages and property search',
            blockId: block.get('id')
          });
        }
      });
      
      // Look for navigation blocks
      blocks.forEach(block => {
        const content = block.get('content');
        if (content && typeof content === 'string' && 
            (content.includes('nav') || content.includes('navbar'))) {
          suggestions.push({
            title: block.get('label') || 'Navigation Bar',
            description: 'Add a professional navigation menu',
            action: 'createNavigation',
            reason: 'Essential for website navigation',
            blockId: block.get('id')
          });
        }
      });
    }
    
    if (selected && selected.get('tagName') === 'div') {
      // Look for grid blocks
      blocks.forEach(block => {
        const content = block.get('content');
        if (content && typeof content === 'string' && 
            (content.includes('uk-grid') || content.includes('grid'))) {
          suggestions.push({
            title: block.get('label') || 'Property Grid',
            description: 'Display multiple property listings in a grid',
            action: 'createPropertyGrid',
            reason: 'Great for showing search results',
            blockId: block.get('id')
          });
        }
      });
      
      // Look for form blocks
      blocks.forEach(block => {
        const content = block.get('content');
        if (content && typeof content === 'string' && 
            (content.includes('form') || content.includes('input'))) {
          suggestions.push({
            title: block.get('label') || 'Contact Form',
            description: 'Add a lead capture form',
            action: 'createContactForm',
            reason: 'Important for lead generation',
            blockId: block.get('id')
          });
        }
      });
    }
    
    displaySuggestions(suggestions);
  }

  // Suggest layout improvements
  function suggestLayout() {
    const suggestions = [];
    const selected = editor.getSelected();
    
    if (selected) {
      const tagName = selected.get('tagName');
      
      if (tagName === 'div') {
        suggestions.push({
          title: 'Add Grid Layout',
          description: 'Convert to responsive grid layout',
          action: 'addGridLayout',
          reason: 'Better responsive design'
        });
      }
      
      if (tagName === 'section') {
        suggestions.push({
          title: 'Add Container',
          description: 'Wrap content in a centered container',
          action: 'addContainer',
          reason: 'Better content width control'
        });
      }
    }
    
    displaySuggestions(suggestions);
  }

  // Generate content suggestions
  function generateContent() {
    const suggestions = [];
    const selected = editor.getSelected();
    
    if (selected && selected.get) {
      const tagName = selected.get('tagName');
      
      if (tagName === 'h1' || tagName === 'h2') {
        suggestions.push({
          title: 'Generate Compelling Headline',
          description: 'Create an engaging headline for your section',
          action: 'generateHeadline',
          reason: 'Improve page engagement'
        });
      }
      
      if (tagName === 'p') {
        suggestions.push({
          title: 'Generate Descriptive Content',
          description: 'Create detailed, engaging content',
          action: 'generateContent',
          reason: 'Better user engagement'
        });
      }
    }
    
    displaySuggestions(suggestions);
  }

  // Suggest real estate specific features
  function suggestRealEstate() {
    const suggestions = [
      {
        title: 'Property Search Hero',
        description: 'Create a hero section with property search',
        action: 'createHeroSection',
        reason: 'Essential for real estate websites'
      },
      {
        title: 'Property Listings Grid',
        description: 'Display property listings with images and details',
        action: 'createPropertyGrid',
        reason: 'Show multiple properties effectively'
      },
      {
        title: 'Agent Contact Form',
        description: 'Create a form for lead generation',
        action: 'createContactForm',
        reason: 'Capture potential buyers/sellers'
      },
      {
        title: 'Property Details Section',
        description: 'Create a detailed property information section',
        action: 'createPropertyDetails',
        reason: 'Show comprehensive property info'
      }
    ];
    
    displaySuggestions(suggestions);
  }

  // Display suggestions in the panel
  function displaySuggestions(suggestions) {
    const suggestionsContainer = document.getElementById('ai-suggestions');
    
    if (suggestions.length === 0) {
      suggestionsContainer.innerHTML = '<p>No suggestions available for the current context.</p>';
      return;
    }
    
    suggestionsContainer.innerHTML = suggestions.map(suggestion => `
      <div class="suggestion-item">
        <h5>${suggestion.title}</h5>
        <p>${suggestion.description}</p>
        <p><em>${suggestion.reason}</em></p>
        <div class="suggestion-actions">
          <button class="suggestion-btn" data-suggestion-action="${suggestion.action}">Execute</button>
          <button class="suggestion-btn" data-learn-more="${suggestion.title}">Learn More</button>
        </div>
      </div>
    `).join('');
  }

  // Create hero section
  function createHeroSection() {
    // Find hero-related components from the block manager
    const blockManager = editor.BlockManager;
    const blocks = blockManager.getAll();
    
    let heroBlock = null;
    blocks.forEach(block => {
      const content = block.get('content');
      if (content && typeof content === 'string' && content.includes('hero')) {
        heroBlock = block;
      }
    });
    
    if (heroBlock) {
      // Add the hero block to the canvas
      const component = editor.addComponent(heroBlock.get('content'));
      editor.select(component);
      closePanel();
      showNotification('Hero section created successfully!', 'success');
    } else {
      // Fallback: create a basic hero section
      const heroComponent = editor.DomComponents.addComponent({
        type: 'div',
        content: `
          <div class="uk-section uk-section-primary uk-light">
            <div class="uk-container">
              <div class="uk-text-center">
                <h1>Welcome to Our Website</h1>
                <p class="uk-text-lead">Find your perfect property with us</p>
                <button class="uk-button uk-button-large uk-button-default">Get Started</button>
              </div>
            </div>
          </div>
        `
      });
      
      editor.select(heroComponent);
      closePanel();
      showNotification('Hero section created successfully!', 'success');
    }
  }

  // Create property grid
  function createPropertyGrid() {
    // Find grid-related components from the block manager
    const blockManager = editor.BlockManager;
    const blocks = blockManager.getAll();
    
    let gridBlock = null;
    blocks.forEach(block => {
      const content = block.get('content');
      if (content && typeof content === 'string' && 
          (content.includes('uk-grid') || content.includes('grid'))) {
        gridBlock = block;
      }
    });
    
    if (gridBlock) {
      // Add the grid block to the canvas
      const component = editor.addComponent(gridBlock.get('content'));
      editor.select(component);
      closePanel();
      showNotification('Property grid created successfully!', 'success');
    } else {
      // Fallback: create a basic grid
      const gridComponent = editor.DomComponents.addComponent({
        type: 'div',
        content: `
          <div class="uk-grid uk-grid-medium uk-child-width-1-3@m" uk-grid>
            <div class="uk-card uk-card-default uk-card-body">
              <h3>Property 1</h3>
              <p>Beautiful property description...</p>
            </div>
            <div class="uk-card uk-card-default uk-card-body">
              <h3>Property 2</h3>
              <p>Another great property...</p>
            </div>
            <div class="uk-card uk-card-default uk-card-body">
              <h3>Property 3</h3>
              <p>Third property listing...</p>
            </div>
          </div>
        `
      });
      
      editor.select(gridComponent);
      closePanel();
      showNotification('Property grid created successfully!', 'success');
    }
  }

  // Create contact form
  function createContactForm() {
    // Find form-related components from the block manager
    const blockManager = editor.BlockManager;
    const blocks = blockManager.getAll();
    
    let formBlock = null;
    blocks.forEach(block => {
      const content = block.get('content');
      if (content && typeof content === 'string' && 
          (content.includes('form') || content.includes('input'))) {
        formBlock = block;
      }
    });
    
    if (formBlock) {
      // Add the form block to the canvas
      const component = editor.addComponent(formBlock.get('content'));
      editor.select(component);
      closePanel();
      showNotification('Contact form created successfully!', 'success');
    } else {
      // Fallback: create a basic contact form
      const formComponent = editor.DomComponents.addComponent({
        type: 'form',
        content: `
          <form class="uk-form-stacked">
            <div class="uk-margin">
              <label class="uk-form-label" for="name">Name</label>
              <div class="uk-form-controls">
                <input class="uk-input" id="name" type="text" placeholder="Your name">
              </div>
            </div>
            <div class="uk-margin">
              <label class="uk-form-label" for="email">Email</label>
              <div class="uk-form-controls">
                <input class="uk-input" id="email" type="email" placeholder="your@email.com">
              </div>
            </div>
            <div class="uk-margin">
              <label class="uk-form-label" for="message">Message</label>
              <div class="uk-form-controls">
                <textarea class="uk-textarea" id="message" rows="4" placeholder="Your message"></textarea>
              </div>
            </div>
            <button class="uk-button uk-button-primary" type="submit">Send Message</button>
          </form>
        `
      });
      
      editor.select(formComponent);
      closePanel();
      showNotification('Contact form created successfully!', 'success');
    }
  }

  // Create navigation
  function createNavigation() {
    // Find navigation-related components from the block manager
    const blockManager = editor.BlockManager;
    const blocks = blockManager.getAll();
    
    let navBlock = null;
    blocks.forEach(block => {
      const content = block.get('content');
      if (content && typeof content === 'string' && 
          (content.includes('nav') || content.includes('navbar'))) {
        navBlock = block;
      }
    });
    
    if (navBlock) {
      // Add the navigation block to the canvas
      const component = editor.addComponent(navBlock.get('content'));
      editor.select(component);
      closePanel();
      showNotification('Navigation created successfully!', 'success');
    } else {
      // Fallback: create a basic navigation
      const navComponent = editor.DomComponents.addComponent({
        type: 'nav',
        content: `
          <nav class="uk-navbar-container" uk-navbar>
            <div class="uk-navbar-left">
              <ul class="uk-navbar-nav">
                <li class="uk-active"><a href="#">Home</a></li>
                <li><a href="#">Properties</a></li>
                <li><a href="#">Agents</a></li>
                <li><a href="#">Contact</a></li>
              </ul>
            </div>
          </nav>
        `
      });
      
      editor.select(navComponent);
      closePanel();
      showNotification('Navigation created successfully!', 'success');
    }
  }

  // Show notification
  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `ai-notification ai-notification-${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#28a745' : '#007bff'};
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      z-index: ${options.zIndex + 20};
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  // Expose show/close globally so Topbar buttons can open the panel without importing this module
  (window as any).showAIAssistant = showPanel;
  (window as any).closeAIAssistant = closePanel;

  console.log('🤖 AI Assistant Panel initialized');
  
  return {
    showPanel,
    closePanel,
    suggestComponents,
    suggestLayout,
    generateContent,
    suggestRealEstate
  };
} 